import React from 'react';
import { Button, Space } from 'antd';
import type { ModalProps, DrawerProps } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useResponsive } from '../../../hooks/useResponsive';
import { withDevModalFilePath } from '../withDevModalFilePath';

/**
 * ResponsiveModal のプロパティ
 */
export interface ResponsiveModalProps extends Omit<ModalProps, 'width'> {
  /** モバイルでドロワーとして表示するか */
  mobileAsDrawer?: boolean;
  /** ドロワーの表示位置（モバイル時） */
  drawerPlacement?: 'bottom' | 'right';
  /** ドロワーの高さ（bottom 配置時、デフォルト: 90vh） */
  drawerHeight?: string | number;
  /** モーダルの幅（デスクトップ時） */
  width?: string | number;
  /** ドロワーの幅（right 配置時、デフォルト: 100%） */
  drawerWidth?: string | number;
}

/**
 * モバイル用ドロワーのスタイル
 */
const mobileDrawerStyles = {
  header: {
    borderBottom: '1px solid #f0f0f0',
    padding: '12px 16px',
  },
  body: {
    padding: '16px',
    overflowY: 'auto' as const,
  },
  footer: {
    borderTop: '1px solid #f0f0f0',
    padding: '12px 16px',
  },
};

const { Modal, Drawer } = withDevModalFilePath('client/src/components/common/responsive/ResponsiveModal.tsx');

/**
 * レスポンシブ対応モーダルコンポーネント
 *
 * デスクトップでは通常のモーダルを表示し、
 * モバイルでは（オプションで）ドロワーとして表示します。
 *
 * @example
 * ```tsx
 * <ResponsiveModal
 *   open={isOpen}
 *   title="設備登録"
 *   mobileAsDrawer
 *   drawerPlacement="bottom"
 *   onCancel={() => setIsOpen(false)}
 *   onOk={handleSubmit}
 * >
 *   <Form>...</Form>
 * </ResponsiveModal>
 * ```
 */
export function ResponsiveModal({
  mobileAsDrawer = true,
  drawerPlacement = 'bottom',
  drawerHeight = '90vh',
  drawerWidth = '100%',
  width = 600,
  children,
  title,
  open,
  onCancel,
  onOk,
  okText,
  cancelText,
  confirmLoading,
  footer,
  destroyOnClose,
  maskClosable = true,
  closable = true,
  ...modalProps
}: ResponsiveModalProps): React.ReactElement {
  const { isMobile } = useResponsive();

  // モバイルでドロワー表示
  if (isMobile && mobileAsDrawer) {
    // フッターをカスタマイズ
    const drawerFooter =
      footer !== undefined ? (
        footer
      ) : footer === null ? null : (
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={(e) => onCancel?.(e as React.MouseEvent<HTMLButtonElement>)}>
            {cancelText ?? 'キャンセル'}
          </Button>
          <Button
            type="primary"
            onClick={(e) => onOk?.(e as React.MouseEvent<HTMLButtonElement>)}
            loading={confirmLoading}
          >
            {okText ?? 'OK'}
          </Button>
        </Space>
      );

    const drawerProps: DrawerProps = {
      open,
      title,
      placement: drawerPlacement,
      height: drawerPlacement === 'bottom' ? drawerHeight : undefined,
      width: drawerPlacement === 'right' ? drawerWidth : undefined,
      onClose: (e) => onCancel?.(e as React.MouseEvent<HTMLButtonElement>),
      closable,
      maskClosable,
      destroyOnClose,
      styles: mobileDrawerStyles,
      footer: drawerFooter as React.ReactNode,
      closeIcon: <CloseOutlined />,
    };

    return <Drawer {...drawerProps}>{children}</Drawer>;
  }

  // デスクトップでモーダル表示
  return (
    <Modal
      open={open}
      title={title}
      width={width}
      onCancel={onCancel}
      onOk={onOk}
      okText={okText}
      cancelText={cancelText}
      confirmLoading={confirmLoading}
      footer={footer}
      destroyOnClose={destroyOnClose}
      maskClosable={maskClosable}
      closable={closable}
      {...modalProps}
    >
      {children}
    </Modal>
  );
}

/**
 * 全画面モーダル（モバイル用）
 *
 * モバイルでは常にフルスクリーンドロワー、
 * デスクトップでは大きめのモーダルとして表示します。
 */
export function FullscreenModal({
  width = '80%',
  ...props
}: ResponsiveModalProps): React.ReactElement {
  return (
    <ResponsiveModal
      {...props}
      width={width}
      mobileAsDrawer
      drawerPlacement="bottom"
      drawerHeight="95vh"
    />
  );
}
